/**
 * FAQ API utilities for integrating with the dual-handler Lambda function
 * Handles organizational settings and API communication
 */

import { getSettings } from './settings';
import { getOrCreateSessionId } from '../config/api';

// API Configuration
export const FAQ_API_URL = process.env.REACT_APP_FAQ_ENDPOINT || 'https://w6kjtm91v8.execute-api.us-east-1.amazonaws.com/test/faq';

// Types
export interface StructuredCitation {
  text: string;
  source_id: string;
  page: number;
}

export type Citation = string | StructuredCitation;

export interface FAQItem {
  question: string;
  answer: string;
  citations: Citation[];
}

export interface AssessmentResult {
  determination: string;
  confidence: number;
  reasoning: string;
  recommendation: string;
  citations: Citation[];
}

export interface FAQPayload {
  interaction_type: 'generate_faqs' | 'assess_situation';
  topic?: string;
  max_results?: number;
  user_input?: string;
  session_id?: string;
  user_id?: string;
  organizational_settings: {
    use_knowledge_base: boolean;
    use_advanced_rag: boolean;
    organization_id?: string;
  };
  field_context_overrides?: FieldContextOverrides; // For Field Context Tuning
}

export interface SourceDocument {
  display_name: string;
  document_name: string;
  uri: string | null;
  pages: number[];
}

// source_documents is now a dict/object keyed by ID, not an array
export interface SourceDocuments {
  [id: string]: SourceDocument;
}

export interface FeedbackRequest {
  enabled: boolean;
  request_id: string;
  interaction_type: string;
  topic: string;
  feedback_endpoint?: string | null;
}

export interface UserFeedback {
  request_id: string;
  rating: number;  // 1-5
  comment?: string;
  timestamp: string;
  user_id?: string;
  organization_id?: string;
}

// Field Context Configuration types for Field Context Tuning
export interface FieldContextOverrides {
  fieldType?: string;
  fieldDescription?: string;
  significance?: string;
  commonRejectionReasons?: string[];
  interviewQuestions?: string[];
  finalPrompt?: string;
  helpText?: string;
  bestPractices?: string[];
  warningFlags?: string[];
  expectedLength?: 'brief' | 'moderate' | 'detailed';
  requiresSpecificity?: boolean;
}

export interface CanonicalContextResponse {
  success: boolean;
  canonical_context?: FieldContextOverrides;
  error?: string;
}

// Field Context Validation types
export interface FieldContextIssue {
  field: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
}

export interface FieldContextValidationResult {
  valid: boolean;
  overall_score: number; // 0-1 score
  quality_label: string; // 'excellent', 'good', 'fair', 'poor'
  issues: FieldContextIssue[];
  summary: string;
  suggestions: string[];
}

export interface FieldContextValidationResponse {
  success: boolean;
  validation?: FieldContextValidationResult;
  error?: string;
}

export interface FAQResponse {
  success: boolean;
  handler_mode: 'kb' | 'standard';
  source: string;
  timestamp: string;
  request_id?: string;
  guidance?: {
    type: string;
    faqs?: FAQItem[];
    assessment?: AssessmentResult;
  };
  source_documents?: SourceDocuments;  // Changed from array to object
  field_context?: FieldContextOverrides;  // Resolved field context used by Lambda
  error?: string;
  // Guardrail intervention fields
  blocked?: boolean;
  block_type?: 'input' | 'output';
  block_reason?: string;
  // Feedback request structure
  feedback_request?: FeedbackRequest;
  // Audit payload for future auditing system
  audit_payload?: {
    request_id: string;
    timestamp: string;
    user_id?: string;
    organization_id?: string;
    interaction_type: string;
    user_input?: string;
    user_input_length: number;
    user_input_included: boolean;
    topic?: string;
    guardrail_triggered: boolean;
    guardrail_intervention?: {
      block_type: 'input' | 'output';
      block_reason: string;
      guardrail_id: string;
      trace_data?: any;
    };
    response_summary?: {
      success: boolean;
      handler_mode: string;
      source: string;
    };
    feedback?: UserFeedback | null;
  };
}

/**
 * Get organizational settings formatted for FAQ API
 */
export const getOrganizationalSettings = () => {
  const settings = getSettings();
  
  return {
    use_knowledge_base: settings.useKnowledgeBase,
    use_advanced_rag: settings.useAdvancedRAG,
    organization_id: 'local_development' // Could be dynamic based on auth
  };
};

/**
 * Generate FAQs using the configured settings
 */
export const generateFAQs = async (
  topic: string = 'foreign-contact-reporting',
  maxResults: number = 3,
  clearanceLevel?: string,
  fieldContextOverrides?: FieldContextOverrides,
  debug: boolean = true
): Promise<FAQResponse> => {
  const orgSettings = getOrganizationalSettings();
  const sessionId = getOrCreateSessionId();

  const payload: FAQPayload = {
    interaction_type: 'generate_faqs',
    topic,
    max_results: maxResults,
    session_id: sessionId,
    user_id: `user_${sessionId.split('_')[1]}`, // Derive user_id from session
    organizational_settings: orgSettings
  };

  // Add field context overrides if provided (for Field Context Tuning)
  if (fieldContextOverrides) {
    payload.field_context_overrides = fieldContextOverrides;
  }

  if (debug) {
    console.log('🔧 FAQ API Request:', {
      endpoint: FAQ_API_URL,
      payload: payload,
      settings: orgSettings,
      expectedHandler: orgSettings.use_knowledge_base ? 'kb' : 'standard',
      mode: orgSettings.use_knowledge_base ? 'AWS Bedrock Knowledge Base' : 'Local Advanced RAG'
    });
  }

  try {
    const response = await fetch(FAQ_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (debug) {
      console.log('📥 FAQ API Response:', {
        handler_mode: result.handler_mode,
        source: result.source,
        success: result.success,
        timestamp: result.timestamp,
        guidance_type: result.guidance?.type,
        faq_count: result.guidance?.faqs?.length || 0
      });
    }
    
    return result;
  } catch (error) {
    console.error('FAQ Generation Error:', error);
    throw error;
  }
};

/**
 * Assess a situation using the configured settings
 */
export const assessSituation = async (
  userInput: string,
  topic: string = 'foreign-contact-reporting',
  clearanceLevel?: string,
  fieldContextOverrides?: FieldContextOverrides,
  debug: boolean = true
): Promise<FAQResponse> => {
  const orgSettings = getOrganizationalSettings();
  const sessionId = getOrCreateSessionId();

  const payload: FAQPayload = {
    interaction_type: 'assess_situation',
    topic,
    user_input: userInput,
    session_id: sessionId,
    user_id: `user_${sessionId.split('_')[1]}`, // Derive user_id from session
    organizational_settings: orgSettings
  };

  // Add field context overrides if provided (for Field Context Tuning)
  if (fieldContextOverrides) {
    payload.field_context_overrides = fieldContextOverrides;
  }

  if (debug) {
    console.log('🔧 Assessment API Request:', {
      endpoint: FAQ_API_URL,
      payload: payload,
      settings: orgSettings,
      expectedHandler: orgSettings.use_knowledge_base ? 'kb' : 'standard',
      mode: orgSettings.use_knowledge_base ? 'AWS Bedrock Knowledge Base' : 'Local Advanced RAG'
    });
  }

  try {
    const response = await fetch(FAQ_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (debug) {
      console.log('📥 Assessment API Response:', {
        handler_mode: result.handler_mode,
        source: result.source,
        success: result.success,
        timestamp: result.timestamp,
        guidance_type: result.guidance?.type,
        assessment_confidence: result.guidance?.assessment?.confidence
      });
    }
    
    return result;
  } catch (error) {
    console.error('Situation Assessment Error:', error);
    throw error;
  }
};

// Test results interface
export interface TestResults {
  connectionStatus: 'success' | 'error';
  settings: ReturnType<typeof getOrganizationalSettings>;
  response?: FAQResponse;
  error?: string;
}

/**
 * Test the FAQ API connection and settings
 */
export const testFAQAPI = async (): Promise<TestResults> => {
  const settings = getOrganizationalSettings();
  
  try {
    const response = await generateFAQs('foreign-contact-reporting', 1);
    
    return {
      connectionStatus: 'success',
      settings,
      response
    };
  } catch (error) {
    return {
      connectionStatus: 'error',
      settings,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Get current configuration info for display
 */
export const getConfigurationInfo = () => {
  const settings = getOrganizationalSettings();

  const mode = settings.use_knowledge_base
    ? 'AWS Bedrock Knowledge Base'
    : 'Local Advanced RAG';

  const description = settings.use_knowledge_base
    ? 'Production-ready vector search using AWS Bedrock Knowledge Base'
    : 'Local document processing with hybrid search algorithms for development';

  return {
    mode,
    description,
    settings,
    endpoint: FAQ_API_URL
  };
};

/**
 * Get canonical field context configuration from dev Lambda
 * Used by Field Context Tuning tool
 */
export const getCanonicalContext = async (
  topic: string
): Promise<CanonicalContextResponse> => {
  try {
    // Use the dev Lambda endpoint with /canonical-context path
    const devEndpoint = FAQ_API_URL.replace('/faq', '/canonical-context');

    const response = await fetch(`${devEndpoint}?topic=${encodeURIComponent(topic)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Get Canonical Context Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error fetching canonical context'
    };
  }
};

/**
 * Validate field context configuration using LLM
 * Used by Field Context Tuning tool
 */
export const validateFieldContext = async (
  topic: string,
  fieldContext: FieldContextOverrides
): Promise<FieldContextValidationResponse> => {
  try {
    // Use the dev Lambda endpoint with /validate-context path
    const devEndpoint = FAQ_API_URL.replace('/faq', '/validate-context');

    const response = await fetch(devEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        topic,
        field_context: fieldContext
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Validate Field Context Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error validating field context'
    };
  }
};